import React, {useEffect, useState} from 'react';
import {MapContainer, TileLayer, useMap, Circle, Rectangle, useMapEvents} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import './AdminPage.css';
import 'leaflet-control-geocoder/dist/Control.Geocoder.css';
import L from 'leaflet';
import 'leaflet-control-geocoder';
import { useRef } from 'react';

import Sidebar from '../Components/Sidebar'; // ← Import Sidebar component
import ArrowLeft from "../Pictures/Icons/arrow_left_24dp_FFFFFF_FILL0_wght400_GRAD0_opsz24.svg";

function MapEvents({ onRightClick, onSetObject, onSetOps, onSetUAV }) {
    const map = useMap();

    useEffect(() => {
        if (!map) return;

        const contextMenuHandler = (e) => {
            L.popup()
                .setLatLng(e.latlng)
                .setContent(`
                    <div class="map-popup-buttons">
                        <button class="set-center-btn">Set View Center Here</button>
                        <button class="set-zone-btn">Set Zone Here</button>
                        <button class="set-ops-btn">Set Deminer Here</button>
                        <button class="set-uav-btn">Set UAV Ops Here</button>
                    </div>
                `)
                .openOn(map);

            setTimeout(() => {
                const centerBtn = document.querySelector('.set-center-btn');
                const zoneBtn = document.querySelector('.set-zone-btn');
                const opsBtn = document.querySelector('.set-ops-btn');
                const uavBtn = document.querySelector('.set-uav-btn');

                if (centerBtn) {
                    centerBtn.addEventListener('click', () => {
                        onRightClick(e.latlng);
                        map.closePopup();
                    });
                }

                if (zoneBtn) {
                    zoneBtn.addEventListener('click', () => {
                        onSetObject(e.latlng);
                        map.closePopup();
                    });
                }

                if (opsBtn) {
                    opsBtn.addEventListener('click', () => {
                        onSetOps(e.latlng);
                        map.closePopup();
                    });
                }

                if (uavBtn) {
                    uavBtn.addEventListener('click', () => {
                        onSetUAV(e.latlng);
                        map.closePopup();
                    });
                }
            }, 0);
        };

        map.on('contextmenu', contextMenuHandler);

        return () => {
            map.off('contextmenu', contextMenuHandler);
        };
    }, [map, onRightClick, onSetObject, onSetOps, onSetUAV]);

    return null;
}

function DraggableMarker({ position, isSelected, onClick, onDragEnd, radius, color }) {
    const map = useMap();
    const [pos, setPos] = useState(position);
    const [isDragging, setIsDragging] = useState(false);

    useEffect(() => {
        setPos(position);
    }, [position]);

    useMapEvents({
        mousemove(e) {
            if (isSelected && isDragging) {
                setPos([e.latlng.lat, e.latlng.lng]);
            }
        },
        mousedown(e) {
            if (isSelected) {
                L.DomEvent.stopPropagation(e);
                setIsDragging(true);
                map.dragging.disable();
            }
        },
        mouseup() {
            if (isSelected && isDragging) {
                setIsDragging(false);
                map.dragging.enable();
                onDragEnd([pos[0], pos[1]]);
            }
        }
    });

    const circleOptions = {
        fillColor: color, // fill color always stays the same
        color: isSelected ? '#00FF00' : color, // border color
        fillOpacity: 0.6,
        weight: isSelected ? 3 : 2 // make the border slightly thicker when selected
    };

    return (
        <Circle
            center={pos}
            radius={radius || 5000}
            pathOptions={circleOptions}
            eventHandlers={{
                click: (e) => {
                    L.DomEvent.stopPropagation(e);
                    onClick();
                }
            }}
        />
    );
}

function DraggableRectangle({ position, isSelected, onClick, onDragEnd, size, color }) {
    const map = useMap();
    const [pos, setPos] = useState(position);
    const [isDragging, setIsDragging] = useState(false);
    const [zoom, setZoom] = useState(map.getZoom());

    useEffect(() => {
        setPos(position);
    }, [position]);

    useMapEvents({
        mousemove(e) {
            if (isSelected && isDragging) {
                setPos([e.latlng.lat, e.latlng.lng]);
            }
        },
        mousedown(e) {
            if (isSelected) {
                L.DomEvent.stopPropagation(e);
                setIsDragging(true);
                map.dragging.disable();
            }
        },
        mouseup() {
            if (isSelected && isDragging) {
                setIsDragging(false);
                map.dragging.enable();
                onDragEnd([pos[0], pos[1]]);
            }
        },
        zoomend() {
            setZoom(map.getZoom());
        }
    });

    // Calculate rectangle bounds based on center position and zoom-adjusted size
    // Base size at zoom level 10, adjust for current zoom
    const baseZoom = 10;
    const zoomFactor = Math.pow(2, baseZoom - zoom);
    const adjustedSize = size * zoomFactor;

    const halfSize = adjustedSize / 2;
    const latOffset = halfSize / 111320; // Convert meters to latitude degrees (approximate)
    const lngOffset = halfSize / (111320 * Math.cos(pos[0] * Math.PI / 180)); // Convert meters to longitude degrees

    const bounds = [
        [pos[0] - latOffset, pos[1] - lngOffset], // Southwest corner
        [pos[0] + latOffset, pos[1] + lngOffset]  // Northeast corner
    ];

    const rectangleOptions = {
        fillColor: color,
        color: isSelected ? '#00FF00' : color,
        fillOpacity: 0.6,
        weight: isSelected ? 3 : 2
    };

    return (
        <Rectangle
            bounds={bounds}
            pathOptions={rectangleOptions}
            eventHandlers={{
                click: (e) => {
                    L.DomEvent.stopPropagation(e);
                    onClick();
                }
            }}
        />
    );
}

function ObjectMarkers({ objects, selectedObject, onObjectClick, onObjectMove }) {
    return objects.map((obj, index) => {
        if (obj.type === 'zone') {
            return (
                <DraggableMarker
                    key={index}
                    position={[obj.lat, obj.lng]}
                    isSelected={selectedObject === index}
                    onClick={() => onObjectClick(index)}
                    onDragEnd={(newPos) => onObjectMove(index, newPos)}
                    radius={obj.radius || 5000}
                    color={obj.color || '#118ACB'}
                />
            );
        } else if (obj.type === 'ops' || obj.type === 'uav') {
            return (
                <DraggableRectangle
                    key={index}
                    position={[obj.lat, obj.lng]}
                    isSelected={selectedObject === index}
                    onClick={() => onObjectClick(index)}
                    onDragEnd={(newPos) => onObjectMove(index, newPos)}
                    size={obj.size || 1500}
                    color={obj.color || '#118ACB'}
                />
            );
        }
        return null;
    });
}

function AdminPage() {
    const colorOptions = [
        { value: '#FF0000', label: 'Controlled by Enemy'},
        { value: '#800080', label: 'Taken Back'},
        { value: '#FFA500', label: 'Combat Zone' },
        { value: '#FFFF00', label: 'Demining Required' },
        { value: '#118ACB', label: 'Demined'}
    ];

    const mapRef = useRef();
    const [objects, setObjects] = useState([]);
    const [selectedObject, setSelectedObject] = useState(null);
    const [activeSetting, setActiveSetting] = useState(null);
    const [showSettingsMenu, setShowSettingsMenu] = useState(false);
    const [fitCenter, setFitCenter] = useState([50.4501, 30.5234]); // Default: Kyiv
    const [isRightSidebarExpanded, setIsRightSidebarExpanded] = useState(false);

    const eventTypeColors = {
        detected: '#FF0000',
        cleared:  '#00FF00',
        scouted: '#118ACB',

        // Add more types as needed
    };
    const exampleHistory = [
        {
            type: 'detected',
            description: 'Anti-Tank Mine detected at (50.450, 30.523)',
            timestamp: '2024-06-04 10:15:00'
        },
        {
            type: 'cleared',
            description: 'Mined Area at (50.455, 30.530)',
            timestamp: '2024-06-04 11:00:00'
        },
        {
            type: 'scouted',
            description: 'Area scouted at (50.460, 30.540)',
            timestamp: '2024-06-04 12:30:00'
        },
        {
            type: 'cleared',
            description: 'Area reclaimed at (50.465, 30.500)',
            timestamp: '2024-06-04 13:30:00'
        }
    ];
    const [history, setHistory] = useState(exampleHistory);

    const historyContent = (
        <div className="scroll-menu">
            <ul>
                {history.map((event, i) => (
                    <li key={i} style={{ color: eventTypeColors[event.type] || '#000' }}>
                        <strong>{event.type.charAt(0).toUpperCase() + event.type.slice(1)}:</strong> {event.description}
                        <span style={{ fontSize: '0.8em', color: '#888', marginLeft: 8 }}>{event.timestamp}</span>
                    </li>
                ))}
            </ul>
        </div>
    );




    const rightbarMap = {
        history: historyContent,
        assets: (
            <div className="scroll-menu">
                <ul>
                    {
                        objects.map((obj, index) => (
                            <li className={"asset-list-element"} key={index} onClick={() => { setSelectedObject(index); setIsRightSidebarExpanded(true); }}>
                                {obj.name || `Zone ${index + 1}`} - Lat: {obj.lat.toFixed(3)}, Lng: {obj.lng.toFixed(3)} {''}
                                <span
                                    style={{
                                        display: 'inline-block',
                                        width: '14px',
                                        height: '14px',
                                        borderRadius: '50%',
                                        backgroundColor: obj.color || '#118ACB',
                                        marginRight: '8px',
                                        verticalAlign: '-px'
                                    }}
                                />
                            </li>
                        ))
                    }
                </ul>
            </div>
        ),
        settings: (
            <div className="scroll-menu">
                <ul>
                    {Array.from({ length: 100 }).map((_, i) => (
                        <li key={i}>Settings item {i + 1}</li>
                    ))}
                </ul>
            </div>
        )
    };


    useEffect(() => {
        if (selectedObject === null && isRightSidebarExpanded) {
            setIsRightSidebarExpanded(false);
        }
    }, [selectedObject]);

    const toggleRightSidebar = () => {
        setIsRightSidebarExpanded(prevState => !prevState);
    };
    const handleFitMap = () => {
        const map = mapRef.current;
        if (map && fitCenter) {
            map.setView(fitCenter, 10);
        }
    };

    const handleRightClick = (latlng) => {
        setFitCenter([latlng.lat, latlng.lng]);
    };

    const handleSetObject = (latlng) => {
        const defaultColor = '#118ACB'; // Default blue color
        setObjects(prev => [...prev, {
            lat: latlng.lat,
            lng: latlng.lng,
            color: defaultColor,
            radius: 5000, // Default radius in meters
            name: `Zone ${prev.filter(obj => obj.type === 'zone').length + 1}`, // Add default name
            type: 'zone'
        }]);
    };

    const handleSetOps = (latlng) => {
        const defaultColor = '#00ffc6'; // Default red color for ops
        setObjects(prev => [...prev, {
            lat: latlng.lat,
            lng: latlng.lng,
            color: defaultColor,
            size: 1500, // Fixed size in meters
            name: `Ops ${prev.filter(obj => obj.type === 'ops').length + 1}`,
            type: 'ops',
            people: 10 // Default people count

        }]);
    };

    const handleSetUAV = (latlng) => {
        const defaultColor = '#0000FF'; // Default orange color for UAV
        setObjects(prev => [...prev, {
            lat: latlng.lat,
            lng: latlng.lng,
            color: defaultColor,
            size: 1500, // Fixed size in meters (smaller than ops)
            name: `UAV ${prev.filter(obj => obj.type === 'uav').length + 1}`,
            type: 'uav',
            people: 5 // Default people count
        }]);
    };

    const handleColorChange = (color) => {
        if (selectedObject !== null) {
            setObjects(prev => prev.map((obj, i) => {
                if (i === selectedObject) {
                    return { ...obj, color: color };
                }
                return obj;
            }));
        }
    };

    const handlePeopleChange = (event) => {
        const newPeople = parseInt(event.target.value, 10);
        if (selectedObject !== null && !isNaN(newPeople) && newPeople >= 0) {
            setObjects(prev => prev.map((obj, i) => {
                if (i === selectedObject) {
                    return { ...obj, people: newPeople };
                }
                return obj;
            }));
        }
    };

    const handleRadiusChange = (event) => {
        const newRadius = parseInt(event.target.value, 10);
        if (selectedObject !== null && !isNaN(newRadius)) {
            setObjects(prev => prev.map((obj, i) => {
                if (i === selectedObject) {
                    return { ...obj, radius: newRadius };
                }
                return obj;
            }));
        }
    };

    const handleObjectClick = (index) => {
        setSelectedObject(prevSelected => prevSelected === index ? null : index);
        setIsRightSidebarExpanded(true);
    };

    const handleDeleteObject = () => {
        if (selectedObject !== null) {
            setObjects(prev => prev.filter((_, index) => index !== selectedObject));
            setSelectedObject(null);
            setIsRightSidebarExpanded(false);
        }
    };

    const handleObjectMove = (index, newPosition) => {
        setObjects(prev => prev.map((obj, i) => {
            if (i === index) {
                return {
                    ...obj,
                    lat: newPosition[0],
                    lng: newPosition[1]
                };
            }
            return obj;
        }));
    };

    const handleToggleSection = (section) => {
        setShowSettingsMenu(prev => !(activeSetting === section && prev));
        setActiveSetting(section);
    };

    const getObjectTypeName = (type) => {
        switch(type) {
            case 'zone': return 'Zone';
            case 'ops': return 'Deminer';
            case 'uav': return 'UAV Operator';
            default: return 'Object';
        }
    };

    return (
        <div className="app-container">
            <Sidebar
                onFitMap={handleFitMap}
                onToggleSection={handleToggleSection}
                activeSetting={activeSetting}
                showSettingsMenu={showSettingsMenu}
                contentMap={rightbarMap}
            />
            <div className="map-container">
                <MapContainer
                    center={fitCenter}
                    zoom={10}
                    scrollWheelZoom={true}
                    style={{height: "100%", width: "100%"}}
                    ref={mapRef}
                >
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <MapEvents
                        onRightClick={handleRightClick}
                        onSetObject={handleSetObject}
                        onSetOps={handleSetOps}
                        onSetUAV={handleSetUAV}
                    />
                    <ObjectMarkers
                        objects={objects}
                        selectedObject={selectedObject}
                        onObjectClick={handleObjectClick}
                        onObjectMove={handleObjectMove}
                    />
                </MapContainer>
            </div>

            <div className={`sidebar right ${isRightSidebarExpanded ? 'expanded' : ''}`}>
                <div className={`right-sidebar-content ${isRightSidebarExpanded ? 'visible' : ''}`}>
                    {selectedObject !== null && (
                        <>
                            <h2>{getObjectTypeName(objects[selectedObject].type)} Details</h2>
                            <div className="detail-item">
                                <label>Name:</label>
                                <input
                                    type="text"
                                    value={objects[selectedObject].name}
                                    onChange={(e) => {
                                        setObjects(prev => prev.map((obj, i) => {
                                            if (i === selectedObject) {
                                                return { ...obj, name: e.target.value };
                                            }
                                            return obj;
                                        }));
                                    }}
                                    className="name-input"
                                />
                            </div>
                            <div className="object-details">
                                <div className="detail-item">
                                    <label>Location:</label>
                                    <p>Lat: {objects[selectedObject].lat.toFixed(6)}</p>
                                    <p>Lng: {objects[selectedObject].lng.toFixed(6)}</p>
                                </div>
                                {objects[selectedObject].type === 'zone' && (
                                    <>
                                        <div className="detail-item">
                                            <label>Radius:</label>
                                            <input
                                                type="number"
                                                value={objects[selectedObject].radius}
                                                onChange={handleRadiusChange}
                                                min="1"
                                                className="radius-input"
                                            />
                                            <span> meters</span>
                                        </div>

                                        <div className="detail-item">
                                            <label>Type:</label>
                                            <div className="custom-select-wrapper">
                                                <select
                                                    className="color-dropdown"
                                                    value={objects[selectedObject].color}
                                                    onChange={(e) => handleColorChange(e.target.value)}
                                                    style={{
                                                        color: objects[selectedObject].color,
                                                        borderColor: objects[selectedObject].color,
                                                        backgroundColor: `${objects[selectedObject].color}1A` // 0.1 opacity in hex (1A)
                                                    }}                                                >
                                                    {colorOptions.map((option) => (
                                                        <option
                                                            key={option.value}
                                                            value={option.value}
                                                            data-color={option.value}
                                                            style={{backgroundColor: option.value}}
                                                        >
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {(objects[selectedObject].type === 'ops' || objects[selectedObject].type === 'uav') && (
                                    <>
                                        <div className="detail-item">
                                            <label>People:</label>
                                            <input
                                                type="number"
                                                value={objects[selectedObject].people || 0}
                                                onChange={handlePeopleChange}
                                                min="1"
                                                className="radius-input"
                                            />
                                            <span> people</span>
                                        </div>
                                        <div className="detail-item">
                                            <button
                                                className="sidebar-btn request-button"
                                                onClick={() => {
                                                alert(`Request sent for ${objects[selectedObject].name}`);
                                                    }}
                                            >
                                                Request {getObjectTypeName(objects[selectedObject].type)} movement
                                            </button>
                                        </div>
                                    </>
                                )}
                                <div className="detail-item">
                                    <button
                                        className="sidebar-btn delete-button"
                                        onClick={handleDeleteObject}
                                    >
                                        Delete {getObjectTypeName(objects[selectedObject].type)}
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <button
                className={`expand-button ${selectedObject === null ? 'disabled' : ''}`}
                onClick={toggleRightSidebar}
                disabled={selectedObject === null}
                aria-label={isRightSidebarExpanded ? "Collapse sidebar" : "Expand sidebar"}
            >
                <img
                    src={ArrowLeft}
                    alt="Toggle sidebar"
                    className={isRightSidebarExpanded ? 'rotated' : ''}
                />
            </button>
        </div>
    );
}

export default AdminPage;